import { useQuery } from "@tanstack/react-query";
import { Layout } from "@/components/layout/Layout";
import { supabase } from "@/integrations/supabase/client";
import { AnchorPageHero } from "@/components/anchor/AnchorPageHero";
import { useHeroOffset } from "@/hooks/useHeroOffset";
import { LogoBar } from "@/components/anchor/LogoBar";
import { CTAStrip } from "@/components/anchor/CTAStrip";
import { WhySection } from "@/components/anchor/WhySection";
import { ContentSections, ContentSection } from "@/components/anchor/ContentSections";
import { usePersonaReorder } from "@/hooks/usePersonaReorder";
import { usePagePersonalisation } from "@/hooks/usePagePersonalisation";
import { SEOHead, organizationJsonLd } from "@/components/SEOHead";
import { StatGrid } from "@/components/research/StatGrid";
import { EmployeeArchetypeWheel } from "@/components/research/EmployeeArchetypeWheel";
import { RecognisedByStrip } from "@/components/RecognisedByStrip";
import { UcVendorLogoStrip } from "@/components/UcVendorLogoStrip";

const Index = () => {
  const { panelRef, onHeroHeight, offsetStyle, offsetClass } = useHeroOffset();
  const pageP = usePagePersonalisation("homepage");

  const { data: settings } = useQuery({
    queryKey: ["page-settings", "home"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("page_settings")
        .select("*")
        .eq("page_slug", "home")
        .eq("published", true)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const ctaButtons = (settings?.cta_buttons as string[] | null) ?? ["request-demo", "speak-expert", "download-resources"];
  const whyItems = (settings?.why_items as string[] | null) ?? [];
  const logoBar = (settings?.logo_bar_urls as { url: string; alt: string }[] | null) ?? [];
  const contentSections = ((settings?.content_sections ?? []) as unknown as ContentSection[]);
  const subLabel = (settings as any)?.sub_label ?? null;

  const { reorderedCtas, reorderedSections } = usePersonaReorder(ctaButtons, contentSections);

  // Apply AI personalisation overrides with CMS fallbacks
  const heroHeadline = pageP?.hero_headline || settings?.hero_headline;
  const heroDescription = pageP?.hero_description || settings?.hero_description;

  console.log("[Index] pageP:", pageP, "heroHeadline:", heroHeadline?.slice(0, 50), "from:", pageP?.hero_headline ? "AI" : "CMS");

  // Reorder content sections to highlight featured solutions
  const finalSections = pageP?.featured_solutions?.length
    ? reorderSectionsByKeywords(reorderedSections, pageP.featured_solutions)
    : reorderedSections;

  return (
    <Layout>
      <SEOHead
        title={settings?.meta_title || "Tango Networks — Mobile UC for MS Teams & Webex"}
        description={
          settings?.meta_description ||
          "Tango Extend is the only global mobile service integrated with Microsoft Teams and Cisco Webex Calling. Add a business eSIM to any smartphone."
        }
        jsonLd={organizationJsonLd()}
      />
      <AnchorPageHero
        onHeightChange={onHeroHeight}
        imageUrl={settings?.hero_image_url}
        headline={heroHeadline}
        description={heroDescription}
        subLabel={subLabel}
      />

      <div ref={panelRef} className={`relative z-10 ${offsetClass} bg-background rounded-t-3xl shadow-[0_-8px_30px_rgba(0,0,0,0.12)]`} style={offsetStyle}>
        <LogoBar logos={logoBar} />
        <RecognisedByStrip />
        <section className="py-10 md:py-12 border-t border-border/60">
          <div className="container mx-auto px-4 max-w-6xl">
            <UcVendorLogoStrip
              heading="Works with the UC platforms you already use"
              size="lg"
            />
          </div>
        </section>
        <CTAStrip buttons={reorderedCtas} />
        <ContentSections sections={finalSections} />
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-4 max-w-6xl space-y-8">
            <header className="text-center max-w-3xl mx-auto space-y-3">
              <h2 className="text-3xl md:text-4xl font-bold text-primary">A solution for every employee</h2>
              <p className="text-muted-foreground">
                Tango Extend adapts to five workforce types — from C-suite to frontline. Select an archetype to see how.
              </p>
            </header>
            <EmployeeArchetypeWheel />
          </div>
        </section>
        <section className="py-16 md:py-20 bg-muted">
          <div className="container mx-auto px-4 max-w-6xl">
            <StatGrid
              title="The market reality"
              intro="Independent research on the gap between today's mobile workforce and the tools they're given."
              theme={["employee-experience", "byod", "esim-adoption", "mobility-investment"]}
              limit={4}
              columns={4}
            />
          </div>
        </section>
        <WhySection headline={settings?.why_headline} description={settings?.why_description} items={whyItems} />
        <CTAStrip buttons={reorderedCtas} variant="dark" />
      </div>
    </Layout>
  );
};

/** Reorder content sections by matching pillar slugs or keywords from AI featured_solutions */
function reorderSectionsByKeywords(sections: ContentSection[], keywords: string[]): ContentSection[] {
  if (!keywords.length) return sections;
  const lowerKw = keywords.map((k) => k.toLowerCase());
  const scored = sections.map((s, i) => {
    const titleLower = (s.title || "").toLowerCase();
    const linkLower = (s.link_url || "").toLowerCase();
    const linkSlug = linkLower.split("/").filter(Boolean).pop() || "";
    let idx = lowerKw.length;
    for (let j = 0; j < lowerKw.length; j++) {
      const term = lowerKw[j];
      if (
        linkSlug === term ||
        linkLower.includes(term) ||
        titleLower.includes(term)
      ) {
        idx = j;
        break;
      }
    }
    return { s, idx, i };
  });
  scored.sort((a, b) => a.idx - b.idx || a.i - b.i);
  return scored.map((x) => x.s);
}

export default Index;
